import emailjs from '@emailjs/browser';
import { push, ref, set } from 'firebase/database';
import { appUrl, emailJsConfig, isEmailJsConfigured } from '@/config';
import { getFirebaseDb } from '@/lib/firebase';
import type { SupportAgent, SupportRequest } from '@/types/support';

function buildLinks(requestId: string, agentId: string, accessToken: string) {
  const base = `${appUrl}/support/request/${requestId}`;
  const qs = `agent=${encodeURIComponent(agentId)}&token=${encodeURIComponent(accessToken)}`;
  return {
    acceptUrl: `${base}?${qs}&action=accept`,
    rejectUrl: `${base}?${qs}&action=reject`,
    openUrl: `${base}?${qs}`,
    dashboardUrl: `${appUrl}/support/dashboard`,
  };
}

export async function notifyAgentsOfRequest(
  request: SupportRequest,
  accessToken: string,
  agents: SupportAgent[],
): Promise<void> {
  const active = agents.filter((a) => a.active && a.email);
  const createdAtLabel = new Date(request.createdAt).toLocaleString();

  for (const agent of active) {
    const links = buildLinks(request.requestId, agent.agentId, accessToken);
    const payload = {
      to_email: agent.email,
      to_name: agent.name,
      customer_name: request.customerName || 'Website visitor',
      issue_summary: request.issueSummary,
      created_at: createdAtLabel,
      request_id: request.requestId,
      accept_url: links.acceptUrl,
      reject_url: links.rejectUrl,
      open_url: links.openUrl,
      dashboard_url: links.dashboardUrl,
      page_url: request.pageUrl,
      priority: request.priority,
    };

    const queueRef = push(ref(getFirebaseDb(), 'emailQueue'));
    await set(queueRef, {
      id: queueRef.key,
      to: agent.email,
      agentId: agent.agentId,
      requestId: request.requestId,
      template: 'support_request',
      payload,
      createdAt: Date.now(),
      status: 'pending',
    });

    if (isEmailJsConfigured()) {
      try {
        await emailjs.send(
          emailJsConfig.serviceId!,
          emailJsConfig.templateId!,
          payload,
          { publicKey: emailJsConfig.publicKey! },
        );
        await set(ref(getFirebaseDb(), `emailQueue/${queueRef.key}/status`), 'sent');
      } catch {
        await set(ref(getFirebaseDb(), `emailQueue/${queueRef.key}/status`), 'failed');
      }
    }
  }
}
